import { useCallback, useEffect, useMemo, useState } from "react";
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

import { api } from "helpers/axios";
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { toast } from "helpers/toast";
import { errorMessage, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Button, Field } from "components/ui";

const hrPermissions = [
  {
    key: 'roles.permission_group.employee_management',
    readonly: true,
    children: [
      { key: 'roles.permission.master_data_update', value: 'employee_management.manage_employees.*', readonly: true },
      { key: 'roles.permission.import', value: 'settings.user_import.*', readonly: true }
    ]
  },
  {
    key: 'roles.permission_group.permission_management',
    children: [
      { key: 'roles.permission.login_users_assign', value: 'settings.login_users.assign' },
      { key: 'roles.permission.groups', value: 'settings.groups.*' },
      { key: 'roles.permission.create_account', value: 'settings.login_users.create' }
    ]
  },
  {
    key: 'roles.permission.actions',
    value: 'settings.actions.*'
  },
  {
    key: 'roles.permission.custom_views',
    value: 'settings.filters.*'
  },
  {
    key: 'roles.permission.balanced_scorecard',
    translate_per_module: true,
    children: [
      { key: 'roles.permission.balanced_scorecard_create', value: 'employee_management.manage_balanced_scorecards.create' },
      { key: 'roles.permission.balanced_scorecard_delete', value: 'employee_management.manage_balanced_scorecards.delete' },
      { key: 'roles.permission.balanced_scorecard_release', value: 'employee_management.manage_balanced_scorecards.release' }
    ]
  },
  {
    key: 'roles.permission.reports',
    children: [
      { key: 'roles.permission.reports_employee_data', value: 'reports.employee_data.*' },
      { key: 'roles.permission.reports_calculation', value: 'reports.calculation.*', modules: ['lob'] },
      { key: 'roles.permission.reports_budgets', value: 'reports.budgets.*', modules: ['lob'] }
    ]
  },
  {
    key: 'roles.permission.evaluation_statistics',
    children: [
      { key: 'roles.permission.evaluation_statistics_cockpit', value: 'evaluation_statistics.cockpit.*' },
      { key: 'roles.permission.evaluation_statistics_assessment_matrix', value: 'evaluation_statistics.assessment_matrix.*', modules: ['db'] },
      // The other statistics pages, labelled like their menu entries (LIX-1777: menus follow these permissions).
      { key: 'global.menu.evaluation_statistics.evaluation_trends', value: 'evaluation_statistics.evaluation_trends.*' },
      { key: 'global.menu.evaluation_statistics.target_monitoring', value: 'evaluation_statistics.target_monitoring.*' },
      { key: 'global.menu.evaluation_statistics.interview_status', value: 'evaluation_statistics.interview_status.*' },
      { key: 'global.menu.evaluation_statistics.evaluator_monitoring', value: 'evaluation_statistics.evaluator_monitoring.*' },
      { key: 'global.menu.evaluation_statistics.group_budget_analysis', value: 'evaluation_statistics.group_budget_analysis.*' },
      { key: 'global.menu.evaluation_statistics.risk_factors_anomalies', value: 'evaluation_statistics.risk_factors_anomalies.*' },
      { key: 'global.menu.evaluation_statistics.strategic_target_alignment', value: 'evaluation_statistics.strategic_target_alignment.*' },
      { key: 'global.menu.evaluation_statistics.system_usage_activities', value: 'evaluation_statistics.system_usage_activities.*' },
    ]
  }
];

const moduleAdminPermissionText = 'Module administrators can manage users, settings, and operational data for this module only. Global system settings remain restricted to global administrators.';
const moduleHRPermissionText = 'HR can manage employees and specific assigned permissions.';
const moduleReviewerPermissionText = 'Module reviewers can view all evaluations and scorecards of every group in this module and run evaluation reports and statistics. They cannot edit, create, delete or release anything, run calculations or send emails.';

const roleSections = [
  {
    section: 'global',
    titleKey: 'roles.section.global',
    titleFallback: 'Global roles',
    roles: [
      { name: 'it-admin', labelKey: 'roles.it-admin', textKey: 'roles.permission_text.it-admin' },
      { name: 'application-admin', labelKey: 'roles.application-admin', textKey: 'roles.permission_text.application-admin' },
    ],
  },
  {
    section: 'lob',
    titleKey: 'roles.section.lob',
    titleFallback: 'LOB module',
    roles: [
      { name: 'module-admin-lob', labelKey: 'roles.module-admin-lob', labelFallback: 'Module Administrator', textKey: 'roles.permission_text.module-admin-lob', textFallback: moduleAdminPermissionText },
      { name: 'module-reviewer-lob', labelKey: 'roles.module-reviewer-lob', labelFallback: 'Module reviewer (read-only)', textKey: 'roles.permission_text.module-reviewer-lob', textFallback: moduleReviewerPermissionText },
      { name: 'hr-lob', labelKey: 'roles.hr-lob', labelFallback: 'Personalverwaltung', textKey: 'roles.permission_text.hr-lob', editable: true, textFallback: moduleHRPermissionText },
      { name: 'evaluator', labelKey: 'roles.evaluator', textKey: 'roles.permission_text.evaluator' },
      { name: 'reviewer', labelKey: 'roles.reviewer', textKey: 'roles.permission_text.reviewer' },
    ],
  },
  {
    section: 'db',
    titleKey: 'roles.section.db',
    titleFallback: 'DB module',
    roles: [
      { name: 'module-admin-db', labelKey: 'roles.module-admin-db', labelFallback: 'Module Administrator', textKey: 'roles.permission_text.module-admin-db', textFallback: moduleAdminPermissionText },
      { name: 'module-reviewer-db', labelKey: 'roles.module-reviewer-db', labelFallback: 'Module reviewer (read-only)', textKey: 'roles.permission_text.module-reviewer-db', textFallback: moduleReviewerPermissionText },
      { name: 'hr-db', labelKey: 'roles.hr-db', labelFallback: 'Personalverwaltung', textKey: 'roles.permission_text.hr-db', editable: true, textFallback: moduleHRPermissionText },
      { name: 'first-evaluator', sourceRole: 'evaluator', labelKey: 'state_employee.first_evaluator', labelFallback: 'Primary reviewer', textKey: 'roles.permission_text.first-evaluator' },
      { name: 'second-evaluator', sourceRole: 'reviewer', labelKey: 'state_employee.second_evaluator', labelFallback: 'Secondary reviewer', textKey: 'roles.permission_text.second-evaluator' },
    ],
  },
];

const defaultEnabledSections = ['global', 'lob', 'db'];
const assignableRoleNames = ['it-admin', 'application-admin', 'module-admin-lob', 'module-admin-db', 'hr-lob', 'hr-db', 'module-reviewer-lob', 'module-reviewer-db'];

function UserList({ users, roleName, savingRoleName, handleUnassignRole }) {
  const { translate, editableTranslate } = useTranslation();

  const [showAll, setShowAll] = useState(false);

  const filteredUsers = useMemo(() => {
    if (showAll || users.length <= 4) return users;
    return users.slice(0, 4);
  }, [users, showAll]);

  return <>
    {
      users.length > 0 && <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
        {
          filteredUsers.map(user => (
            <div key={user.hash_id} className={`role-user-chip role-user-chip-${roleName} has-remove-btn ${savingRoleName === roleName ? 'disabled' : ''}`} data-testid={`role-user-${roleName}-${user.hash_id}`}>
              <span data-tooltip-id="user-email-tooltip" data-tooltip-content={user.email}>{user.full_name}</span>
              <Button
                variant="ghost"
                size="inline"
                icon="bi-x"
                className="role-user-chip-remove"
                disabled={savingRoleName === roleName}
                onClick={() => handleUnassignRole(roleName, user.hash_id)}
                aria-label={translate('button.delete', 'Löschen')} data-testid={`unassign-user-${roleName}-${user.hash_id}`}
              />
            </div>
          ))
        }
        {
          (users.length > 4 && !showAll) && <div data-testid={`view-all-users-${roleName}`} onClick={() => savingRoleName === roleName ? null : setShowAll(true)} className={`role-user-chip role-user-chip-${roleName} ${savingRoleName === roleName ? 'disabled' : 'cursor-pointer'}`} data-tooltip-id="view-all-users-tooltip">
            <span>+ {users.length - 4}</span>
          </div>
        }
      </div>
    }
    {
      users.length === 0 && <div className="mt-3">
        <span className="text-muted">{editableTranslate('roles.no_assigned_users', 'Keine Nutzenden zugewiesen.')}</span>
      </div>
    }
  </>;
}

export default function Roles({ enabledSection = defaultEnabledSections }) {
  const { translate, editableTranslate } = useTranslation();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [enabledModules, setEnabledModules] = useState([]);
  const [roleUsers, setRoleUsers] = useState({});
  const [searchOptions, setSearchOptions] = useState({});
  const [searchLoadingRole, setSearchLoadingRole] = useState(null);
  const [savingRoleName, setSavingRoleName] = useState(null);
  const [checkedHrPermissions, setCheckedHrPermissions] = useState({
    'hr-lob': [],
    'hr-db': [],
  });

  const visibleSections = useMemo(() => {
    const normalized = enabledSection.flatMap(section => {
      if (section === 'admin') return ['global'];
      if (section === 'user') return ['lob', 'db'];
      return [section];
    });

    const licensedSections = ['global', ...enabledModules];

    return roleSections.filter(section => normalized.includes(section.section) && licensedSections.includes(section.section));
  }, [enabledSection, enabledModules]);

  const visibleEditableRoleNames = useMemo(() => {
    return visibleSections
      .flatMap(section => section.roles)
      .filter(role => role.editable)
      .map(role => role.name);
  }, [visibleSections]);

  const globalSections = useMemo(() => {
    return visibleSections.filter(section => section.section === 'global');
  }, [visibleSections]);

  const moduleSections = useMemo(() => {
    return visibleSections.filter(section => section.section !== 'global');
  }, [visibleSections]);

  const fetchRoleModules = useCallback(async (showLoading = true) => {
    setError(null);
    if (showLoading) {
      setLoadingModules(true);
    }

    try {
      const { data, status } = await api.get('/settings/roles?include=users');
      if (status === 200) {
        if (showLoading) {
          setEnabledModules(data?.meta?.modules || []);
        }
        setRoleUsers(Object.fromEntries((data?.data || []).map(role => [
          role.name,
          role?.users?.data || role?.users || [],
        ])));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch role modules'));
      scrollToTop();
      Sentry.captureException(err);
    }

    if (showLoading) {
      setLoadingModules(false);
    }
  }, []);

  const fetchHrPermissions = useCallback(async () => {
    if (loadingModules) {
      return;
    }

    setLoadingPermissions(true);

    if (visibleEditableRoleNames.length === 0) {
      setLoadingPermissions(false);
      return;
    }

    try {
      const responses = await Promise.all(visibleEditableRoleNames.map(roleName => api.get(`/settings/roles/${roleName}/permissions`)));
      setCheckedHrPermissions(Object.fromEntries(responses.map((response, index) => [
        visibleEditableRoleNames[index],
        response.data.data,
      ])));
    } catch (err) {
      setError(errorMessage(err, 'fetch HR permissions'));
      scrollToTop();
      Sentry.captureException(err);
    }

    setLoadingPermissions(false);
  }, [loadingModules, visibleEditableRoleNames]);

  useEffect(() => {
    fetchRoleModules();
  }, [fetchRoleModules]);

  useEffect(() => {
    fetchHrPermissions();
  }, [fetchHrPermissions]);

  const handleSaveHrPermissions = async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all(visibleEditableRoleNames.map(roleName => api.patch(`/settings/roles/${roleName}/permissions`, {
        permissions: checkedHrPermissions[roleName] || []
      })));
      toast.success(translate('form.success.saved'));
    } catch (err) {
      setError(errorMessage(err, 'save HR permissions'));
      scrollToTop();
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const handleSearchUser = (roleName) => async (query) => {
    setSearchLoadingRole(roleName);

    try {
      const { data, status } = await api.get('/users', {
        params: {
          q: query,
          in: `role-search:${roleName}`,
          roles: 'all',
          limit: 20,
        },
      });

      if (status === 200) {
        setSearchOptions(prev => ({
          ...prev,
          [roleName]: (data?.data || []).map(user => ({
            ...user,
            id: user.hash_id,
            label: [user.full_name, user.email].filter(Boolean).join(' - '),
          })),
        }));
      }
    } catch (err) {
      setError(errorMessage(err, 'search users'));
      Sentry.captureException(err);
    }

    setSearchLoadingRole(null);
  };

  const handleAssignRole = async (roleName, selected) => {
    if (!selected?.[0]) {
      return;
    }

    setSavingRoleName(roleName);
    setError(null);

    try {
      await api.post('/settings/roles/assign', {
        role_name: roleName,
        user_hash_id: selected[0].hash_id,
      });
      toast.success(translate('form.success.saved'));
      setSearchOptions(prev => ({ ...prev, [roleName]: [] }));
      await fetchRoleModules(false);
    } catch (err) {
      setError(errorMessage(err, 'assign role'));
      scrollToTop();
      Sentry.captureException(err);
    }

    setSavingRoleName(null);
  };

  const handleUnassignRole = async (roleName, userHashId) => {
    setSavingRoleName(roleName);
    setError(null);

    try {
      await api.post('/settings/roles/unassign', {
        role_name: roleName,
        user_hash_id: userHashId,
      });
      toast.success(translate('form.success.saved'));
      await fetchRoleModules(false);
    } catch (err) {
      setError(errorMessage(err, 'unassign role'));
      scrollToTop();
      Sentry.captureException(err);
    }

    setSavingRoleName(null);
  };

  const handleHrPermissionChange = (roleName, key, isChecked, permission, isChild = false) => {
    const vals = [];

    if (isChild) {
      const filtered = permission.children.filter(c => c.key === key);
      vals.push(filtered[0].value);
    } else if (permission?.value) {
      vals.push(permission.value);
    }

    if (!isChild && permission?.children) {
      for (const child of permission.children) {
        vals.push(child.value);
      }
    }

    if (isChecked) {
      setCheckedHrPermissions(prev => ({
        ...prev,
        [roleName]: Array.from(new Set([...(prev[roleName] || []), ...vals]))
      }));
    } else {
      setCheckedHrPermissions(prev => ({
        ...prev,
        [roleName]: (prev[roleName] || []).filter(p => !vals.includes(p))
      }));
    }
  };

  const isChecked = (roleName, key, permission, isChild = false) => {
    const rolePermissions = checkedHrPermissions[roleName] || [];

    if (isChild) {
      const filtered = permission.children.filter(c => c.key === key);
      return rolePermissions.includes(filtered[0].value);
    }

    if (permission?.value) {
      return rolePermissions.includes(permission.value);
    }

    let countChildrenChecked = 0;
    for (const child of permission.children) {
      if (rolePermissions.includes(child.value)) countChildrenChecked++;
    }
    if (countChildrenChecked === permission.children.length) return true;
    return false;
  };

  const renderRoleAssignment = (roleName, withBorderBottom = false) => {
    if (!assignableRoleNames.includes(roleName)) {
      return null;
    }

    const users = roleUsers[roleName] || [];

    return <div className={`mt-3 ${withBorderBottom ? 'border-bottom pb-3' : ''}`}>
      <Field label={editableTranslate('roles.add_user_to_role', 'Benutzer zur Rolle hinzufügen')}>
        <AsyncTypeahead
          id={`${roleName}-user-search`}
          className="max-w-450 role-user-search"
          isLoading={searchLoadingRole === roleName || savingRoleName === roleName}
          labelKey="label"
          minLength={2}
          onSearch={handleSearchUser(roleName)}
          onChange={(selected) => handleAssignRole(roleName, selected)}
          options={searchOptions[roleName] || []}
          placeholder={translate('evaluation.search_by_email_or_full_name', 'Suche nach E-Mail/vollständigem Namen')}
          selected={[]}
          useCache={false}
          disabled={savingRoleName === roleName}
          renderMenuItemChildren={(option) => (
            <div>
              <div className="font-weight-bold">{option.full_name}</div>
              <small>{option.email}</small>
            </div>
          )}
        />
      </Field>
      <UserList users={users} roleName={roleName} savingRoleName={savingRoleName} handleUnassignRole={handleUnassignRole} />
    </div>;
  };

  const renderRoleCard = (section, role, style) => (
    <div key={role.name} id={role.name} className="mb-4" style={style}>
      <div className="card h-100">
        <div>
          <div className={`d-flex align-items-center justify-content-between roles-title-div ${'role-' + role.name}`}>
            <h5 className="mb-0"><i className="bi bi-circle-fill"></i> {editableTranslate(role.labelKey, role.labelFallback)}</h5>
          </div>
          <div className="card-body">
            <div>
              {editableTranslate(role.textKey, role.textFallback)}
            </div>
            {renderRoleAssignment(role.name, role?.editable)}
            {
              role.editable && <div className="mt-3">
                <div className="mb-3">
                  {editableTranslate('roles.permission_text.assigned_permissions')}
                </div>
                {
                  hrPermissions.filter(perm => perm?.modules ? perm.modules.includes(section.section) : true)
                    .map(perm => perm?.children ? { ...perm, children: perm.children.filter(child => child?.modules ? child.modules.includes(section.section) : true) } : perm)
                    .map((perm, i) => <div key={i} className={i < hrPermissions.length - 1 ? `mb-3` : ''}>
                      <div className="form-check">
                        <input onChange={e => handleHrPermissionChange(role.name, perm.key, e.target.checked, perm)} className={`form-check-input`} type="checkbox" id={`${role.name}-${perm.key}`} checked={isChecked(role.name, perm.key, perm)} disabled={perm?.readonly || false} data-testid={`role-permission-${role.name}-${perm.key}`} />
                        <label className="form-check-label ml-1" htmlFor={`${role.name}-${perm.key}`}>
                          {editableTranslate(perm.key + (perm?.translate_per_module ? '_' + section.section : ''))}
                        </label>
                      </div>

                      {
                        perm?.children?.map((child, j) => <div key={j} className="form-check mt-2 ml-4">
                            <input onChange={e => handleHrPermissionChange(role.name, child.key, e.target.checked, perm, true)} className={`form-check-input`} type="checkbox" id={`${role.name}-${child.key}`} checked={isChecked(role.name, child.key, perm, true)} disabled={child?.readonly || false} data-testid={`role-permission-${role.name}-${child.key}`} />
                            <label className="form-check-label ml-1" htmlFor={`${role.name}-${child.key}`}>
                              {editableTranslate(child.key)}
                            </label>
                          </div>)
                      }
                    </div>)
                }
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );

  const renderSectionTitle = (section, style) => (
    <h4 key={`${section.section}-title`} className="mt-4 mb-3" style={style}>{editableTranslate(section.titleKey, section.titleFallback)}</h4>
  );

  const renderRoleSection = (section) => (
    <div key={section.section}>
      {renderSectionTitle(section)}
      {section.roles.map(role => renderRoleCard(section, role))}
    </div>
  );

  // One grid for the module sections, so the cards of the same row share a height and LOB and DB
  // line up side by side instead of drifting apart (stacked per module on small screens).
  const renderModuleSections = () => (
    <div className="roles-module-grid" style={{ '--role-columns': moduleSections.length }}>
      {
        moduleSections.flatMap((section, column) => [
          renderSectionTitle(section, { '--role-column': column + 1, '--role-row': 1 }),
          ...section.roles.map((role, row) => renderRoleCard(section, role, { '--role-column': column + 1, '--role-row': row + 2 })),
        ])
      }
    </div>
  );

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between">
            <h4>{editableTranslate('global.menu.settings.roles')}</h4>
          </div>
          <p className="section-description">{editableTranslate('global.menu.settings.role.description')}</p>

          <div className="row">
            <div className={'col'}>
              <div className="alert alert-info">
                <span className="bi bi-info-circle-fill mr-2"></span>
                {editableTranslate('roles.notes')}
              </div>
              {
                error && <div className="mt-3">
                  <ErrorAlert error={error} onClose={() => setError(null)} />
                </div>
              }
              {
                (loadingModules || loadingPermissions) && <div data-testid="loading">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="sr-only">Wird geladen...</span>
                  </div>
                </div>
              }
              {
                !loadingModules && !loadingPermissions && <>
                  {globalSections.map(renderRoleSection)}
                  {renderModuleSections()}
                </>
              }

              {
                !loadingModules && !loadingPermissions && visibleEditableRoleNames.length > 0 && <div className="settings-sticky-footer">
                  <hr className="mt-0" />
                  <Button variant="primary" onClick={handleSaveHrPermissions} loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
                </div>
              }

              <Tooltip id="user-email-tooltip" clickable={true} />
              <Tooltip id="view-all-users-tooltip" clickable={true} content={editableTranslate('roles.view_all_users')} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
